import React, { useEffect, useMemo, useState } from "react";
import Offcanvas from "../../../../components/common/SwipeableOffcanvas.jsx";
import { FaSearch, FaPlus, FaCheck, FaTimes, FaDumbbell } from "react-icons/fa";
import { VideoThumb } from "../VideoExercicio.jsx";
import ExercicioEvolucaoOffcanvas from "../ExercicioEvolucaoOffcanvas.jsx";
import {
  searchExercises,
  MUSCLE_GROUPS,
  libToExercicio,
} from "./exerciseLibraryService";
import s from "./CriarTreinoManual.module.scss";

const EMPTY = [];
export default function ExerciseLibrary({
  show,
  onHide,
  defaultGroup = null,
  defaultGroups = EMPTY,
  treinoName = "",
  selectedIds = EMPTY,
  onAdd,
  count = 0,
  uid,
  perfil,
}) {
  const groupsKey = (
    defaultGroups.length ? defaultGroups : defaultGroup ? [defaultGroup] : []
  ).join("|");
  const [group, setGroup] = useState("origin");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [exercise, setExercise] = useState(null);
  const [added, setAdded] = useState([]);
  useEffect(() => {
    if (show) {
      setGroup(groupsKey ? "origin" : "");
      setQuery("");
      setAdded([]);
      setExercise(null);
    }
  }, [show, groupsKey]);
  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    setLoading(true);
    setError(false);
    const timer = setTimeout(async () => {
      try {
        const data = await searchExercises({
          query,
          group: group === "origin" ? null : group,
          groups:
            group === "origin" ? groupsKey.split("|").filter(Boolean) : [],
          limit: 200,
        });
        if (alive) setRows(data);
      } catch {
        if (alive) {
          setError(true);
          setRows([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [show, query, group, groupsKey, retry]);
  const selected = useMemo(
    () => new Set([...selectedIds, ...added].map(String)),
    [selectedIds, added]
  );
  const add = (ex) => {
    if (selected.has(String(ex.id))) return;
    setAdded((prev) => [...prev, ex.id]);
    onAdd?.(ex);
  };
  return (
    <>
      <Offcanvas
        show={show}
        onHide={onHide}
        placement="end"
        className={s.libOffcanvas}
      >
        <Offcanvas.Header className={s.libHeader}>
          <div>
            <Offcanvas.Title>Biblioteca de exercícios</Offcanvas.Title>
            <p>
              {[treinoName, groupsKey.replaceAll("|", ", ")]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <button
            className={s.bBack}
            onClick={onHide}
            aria-label="Fechar biblioteca"
          >
            <FaTimes />
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body className={s.libBody}>
          <label className={s.searchWrap}>
            <FaSearch className={s.searchIcon} />
            <input
              className={s.searchInput}
              aria-label="Buscar exercícios"
              placeholder="Buscar nome ou grupo muscular"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className={s.libFilters} aria-label="Grupos musculares">
            <button
              className={`${s.chip} ${group === "" ? s.chipActive : ""}`}
              onClick={() => setGroup("")}
            >
              Todos
            </button>
            {groupsKey && (
              <button
                className={`${s.chip} ${
                  group === "origin" ? s.chipActive : ""
                }`}
                onClick={() => setGroup("origin")}
              >
                Deste treino
              </button>
            )}
            {MUSCLE_GROUPS.map((g) => (
              <button
                key={g}
                aria-pressed={group === g}
                className={`${s.chip} ${group === g ? s.chipActive : ""}`}
                onClick={() => setGroup(g)}
              >
                {g}
              </button>
            ))}
          </div>
          <div className={s.libList} aria-busy={loading}>
            {loading && (
              <p className={s.libHint} role="status">
                Carregando exercícios…
              </p>
            )}
            {error && (
              <div className={s.libHint} role="alert">
                <p>Não foi possível carregar a biblioteca.</p>
                <button
                  className={s.btnSecondary}
                  onClick={() => setRetry((v) => v + 1)}
                >
                  Tentar novamente
                </button>
              </div>
            )}
            {!loading && !error && !rows.length && (
              <div className={s.libHint}>
                <p>Nenhum exercício encontrado.</p>
                <button
                  className={s.btnSecondary}
                  onClick={() => {
                    setQuery("");
                    setGroup("");
                  }}
                >
                  Limpar filtros
                </button>
              </div>
            )}
            {!loading &&
              !error &&
              rows.map((ex) => (
                <div className={s.libItem} key={ex.id}>
                  <VideoThumb
                    url={ex.video_url_1}
                    thumbnail={ex.thumbnail_url}
                    width={58}
                    height={58}
                    onClick={() => setExercise(ex)}
                  />
                  <button className={s.libInfo} onClick={() => setExercise(ex)}>
                    <strong className={s.libName}>{ex.name}</strong>
                    <span className={s.libMuscle}>
                      {[ex.muscle_group, ex.equipment, ex.level]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                  <button
                    className={`${s.libAddBtn} ${
                      selected.has(String(ex.id)) ? s.libAdded : ""
                    }`}
                    disabled={selected.has(String(ex.id))}
                    onClick={() => add(ex)}
                    aria-label={`${
                      selected.has(String(ex.id)) ? "Adicionado" : "Adicionar"
                    } ${ex.name}`}
                  >
                    {selected.has(String(ex.id)) ? <FaCheck /> : <FaPlus />}
                  </button>
                </div>
              ))}
          </div>
          <div className={s.libCart}>
            <FaDumbbell />
            <span>{count} exercícios adicionados</span>
            <button className={s.btnPrimary} onClick={onHide}>
              Concluir
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
      <ExercicioEvolucaoOffcanvas
        show={!!exercise}
        onHide={() => setExercise(null)}
        uid={uid}
        perfil={perfil}
        exercicio={exercise ? libToExercicio(exercise) : null}
        readOnly
        actionLabel={
          selected.has(String(exercise?.id))
            ? "Adicionado ao treino"
            : "Adicionar ao treino"
        }
        actionDisabled={selected.has(String(exercise?.id))}
        onAction={() => add(exercise)}
      />
    </>
  );
}
